import { useEffect, useState } from "react";
import { listDocDocuments } from "@/lib/document/documentsClient";
import type { DocDocumentListItem } from "@/lib/document/documentTypes";
import {
  PROPOSAL_DOC_REF_PAGE_KIND,
  type ProposalAlternatePage,
} from "@/lib/opportunity/proposalAlternateTypes";
import { normalizeAlternatePages } from "@/lib/opportunity/proposalAlternateApply";

type Props = {
  draftPages: ProposalAlternatePage[];
  onChange: (pages: ProposalAlternatePage[]) => void;
};

export function ProposalAlternateDocPagesEditor({ draftPages, onChange }: Props) {
  const [publishedDocs, setPublishedDocs] = useState<DocDocumentListItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    void listDocDocuments({ publishedOnly: true }).then((r) => {
      if (cancelled || !r.ok) return;
      setPublishedDocs(r.data.documents);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const sorted = normalizeAlternatePages(draftPages);
  const docPages = sorted.filter((p) => p.page_kind === PROPOSAL_DOC_REF_PAGE_KIND);
  const attachedIds = new Set(docPages.map((p) => p.doc_document_id).filter(Boolean));

  function updateDocPages(nextDocPages: ProposalAlternatePage[]) {
    const builtin = sorted.filter((p) => p.page_kind !== PROPOSAL_DOC_REF_PAGE_KIND);
    onChange(normalizeAlternatePages([...builtin, ...nextDocPages]));
  }

  function addDoc(docId: string) {
    if (attachedIds.has(docId)) return;
    updateDocPages([
      ...docPages,
      {
        page_kind: PROPOSAL_DOC_REF_PAGE_KIND,
        doc_document_id: docId,
        sort_order: sorted.length,
        included: true,
      },
    ]);
  }

  function removeDoc(docId: string) {
    updateDocPages(docPages.filter((p) => p.doc_document_id !== docId));
  }

  function toggleDoc(docId: string) {
    updateDocPages(
      docPages.map((p) =>
        p.doc_document_id === docId ? { ...p, included: !p.included } : p,
      ),
    );
  }

  const available = publishedDocs.filter((d) => !attachedIds.has(d.id));

  return (
    <div className="space-y-2 border-t border-gray-100 pt-3 dark:border-zinc-800">
      <p className="text-xs font-medium text-gray-700 dark:text-zinc-300">
        Attached documents (published)
      </p>
      <p className="text-[10px] text-gray-500 dark:text-zinc-400">
        Embed Document Builder pages in this alternate. Fill fields render empty until a future
        proposal fill pass — use mostly static contract text for now.
      </p>
      {docPages.length === 0 ? (
        <p className="text-[10px] text-gray-500 dark:text-zinc-400">No documents attached.</p>
      ) : (
        <ul className="space-y-1">
          {docPages.map((page) => {
            const doc = publishedDocs.find((d) => d.id === page.doc_document_id);
            return (
              <li
                key={page.doc_document_id}
                className="flex flex-wrap items-center gap-2 rounded border border-gray-100 px-2 py-1 text-xs dark:border-zinc-800"
              >
                <label className="flex flex-1 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={page.included}
                    onChange={() => toggleDoc(page.doc_document_id!)}
                  />
                  {doc?.name ?? page.doc_document_id}
                </label>
                <button
                  type="button"
                  onClick={() => removeDoc(page.doc_document_id!)}
                  className="text-red-700 dark:text-red-300"
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {available.length > 0 ? (
        <label className="block text-xs">
          <span className="text-gray-500">Add document</span>
          <select
            defaultValue=""
            onChange={(e) => {
              const id = e.target.value;
              if (id) addDoc(id);
              e.currentTarget.value = "";
            }}
            className="mt-1 w-full rounded border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
          >
            <option value="">Select published document…</option>
            {available.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="text-[10px] text-gray-500 dark:text-zinc-400">
          Publish documents in the Document library to attach them here.
        </p>
      )}
    </div>
  );
}
